// ============================================================================
// ENHANCED TABLE SERVICE - ENTERPRISE API VERSION
// ============================================================================
// This service handles the NEW table creation features (special rules, private tables, etc.)
// All writes go through Cloud Function APIs.
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  SpecialRules,
  DEFAULT_SPECIAL_RULES,
  ExtendedTableData,
  CreateTableRequest,
  GameVariant,
  BettingStructure,
  generatePrivateCode,
} from '../types/GameState';
import { API_BASE } from '../utils/apiBase';

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function apiCall<T>(endpoint: string, body: object): Promise<T> {
  const token = await getAuthToken();
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `API call failed: ${response.status}`);
  }
  
  return data;
}

// ============================================================================
// TYPES
// ============================================================================

export interface CreateEnhancedTableResult {
  success: boolean;
  tableId?: string;
  error?: string;
}

export interface JoinPrivateTableResult {
  success: boolean;
  tableId?: string;
  error?: string;
}

export interface TableSearchFilters {
  currency?: 'playcoins' | 'pkcash';
  minBlind?: number;
  maxBlind?: number;
  hasSeatsAvailable?: boolean;
  status?: 'waiting' | 'playing';
  hasSpecialRules?: boolean;
  isPrivate?: boolean;
}

// ============================================================================
// TABLE NAME GENERATION
// ============================================================================

/**
 * Generate a fun, memorable table name
 */
function generateTableName(currency: 'playcoins' | 'pkcash'): string {
  const adjectives = [
    'Lucky', 'Golden', 'Royal', 'Diamond', 'Silver', 'Blazing', 'Mystic',
    'Thunder', 'Shadow', 'Crystal', 'Neon', 'Cosmic', 'Turbo', 'Elite',
    'Midnight', 'Sunrise', 'Velvet', 'Electric', 'Phantom', 'Supreme'
  ];
  
  const nouns = [
    'Aces', 'Kings', 'Queens', 'Jacks', 'Flush', 'Straight', 'River',
    'Flop', 'Showdown', 'Bluff', 'Stakes', 'Chips', 'Cards', 'Table',
    'Pot', 'Deck', 'Hand', 'Deal', 'Raise', 'Call'
  ];
  
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const number = Math.floor(Math.random() * 99) + 1;
  
  const prefix = currency === 'pkcash' ? '' : '';

  
  return `${prefix} ${adjective} ${noun} #${number}`;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if table has any special rules enabled
 */
function hasAnySpecialRules(rules: SpecialRules): boolean {
  return (
    rules.bombPotEnabled ||
    rules.sevenTwoEnabled ||
    rules.straddleEnabled ||
    rules.runItTwiceEnabled ||
    rules.allInInsuranceEnabled
  );
}

/**
 * Get list of enabled rule names for logging
 */
function getEnabledRuleNames(rules: SpecialRules): string[] {
  const enabled: string[] = [];
  if (rules.bombPotEnabled) enabled.push('Bomb Pot');
  if (rules.sevenTwoEnabled) enabled.push('7-2 Game');
  if (rules.straddleEnabled) enabled.push('Straddle');
  if (rules.runItTwiceEnabled) enabled.push('Run It Twice');
  if (rules.allInInsuranceEnabled) enabled.push('All-In Insurance');
  return enabled;
}

/**
 * Generate tags for search/filtering
 */
function generateTags(
  rules: SpecialRules, 
  isPrivate: boolean, 
  currency: 'playcoins' | 'pkcash',
  voiceChatEnabled: boolean
): string[] {
  const tags: string[] = [currency];
  
  if (isPrivate) tags.push('private');
  if (voiceChatEnabled) tags.push('voice');
  if (rules.bombPotEnabled) tags.push('bombpot');
  if (rules.sevenTwoEnabled) tags.push('72game');
  if (rules.straddleEnabled) tags.push('straddle');
  if (rules.runItTwiceEnabled) tags.push('runittwice');
  if (rules.allInInsuranceEnabled) tags.push('insurance');
  if (hasAnySpecialRules(rules)) tags.push('special');
  
  return tags;
}

// ============================================================================
// CREATE ENHANCED TABLE - API VERSION
// ============================================================================

/**
 * Create a new table with enhanced features (special rules, private tables, voice chat, etc.)
 *  ENTERPRISE: Uses server API for table creation

 */
export async function createEnhancedTable(
  request: CreateTableRequest & { 
    stateCode?: string;
    //  NEW: Rake waiver from agent bypass code

    rakeWaived?: boolean;
    rakeWaiverCode?: string;
    rakeWaiverAgentId?: string;
    //  PREMIUM: Table theme settings

    tableTheme?: 'classic' | 'premium';
    dealerImageUrl?: string;
    // 🏠 HOME GAME: host-banked session chips (private tables only)
    chipMode?: 'wallet' | 'session';
  }
): Promise<CreateEnhancedTableResult> {
  try {
    const {
      ownerId,
      currency,
      smallBlind,
      bigBlind,
      customName,
      maxSeats = 8,
      minBuyIn,
      maxBuyIn,
      isPrivate = false,
      privateCode,
      specialRules,
      voiceChatEnabled = false,
      stateCode, //  NEW: Destructure stateCode

      //  NEW: Rake waiver fields

      rakeWaived = false,
      rakeWaiverCode,
      rakeWaiverAgentId,
      //  PREMIUM: Table theme settings

      tableTheme = 'classic',
      dealerImageUrl,
    } = request;

    // Validate inputs
    if (!ownerId) {
      return { success: false, error: 'Owner ID is required' };
    }
    if (smallBlind <= 0 || bigBlind <= 0) {
      return { success: false, error: 'Blinds must be greater than 0' };
    }
    if (bigBlind < smallBlind) {
      return { success: false, error: 'Big blind must be >= small blind' };
    }
    if (maxSeats < 2 || maxSeats > 10) {
      return { success: false, error: 'Max seats must be between 2 and 10' };
    }

    // Calculate buy-in defaults (standard: 20-100x BB)
    const calculatedMinBuyIn = minBuyIn ?? Math.round(bigBlind * 20);
    const calculatedMaxBuyIn = maxBuyIn ?? Math.round(bigBlind * 100);

    // Validate buy-in range
    if (calculatedMinBuyIn > calculatedMaxBuyIn) {
      return { success: false, error: 'Min buy-in cannot be greater than max buy-in' };
    }

    // Generate or use provided table name
    const tableName = customName?.trim() || generateTableName(currency);

    // Generate private code if needed
    let finalPrivateCode: string | undefined;
    if (isPrivate) {
      finalPrivateCode = privateCode?.trim() || generatePrivateCode();
    }

    // Merge special rules with defaults
    const finalSpecialRules: SpecialRules = {
      ...DEFAULT_SPECIAL_RULES,
      ...(specialRules || {}),
    };

    // Validate special rules
    if (finalSpecialRules.sevenTwoBounty < 0) {
      return { success: false, error: '7-2 bounty cannot be negative' };
    }
    if (finalSpecialRules.bombPotAnte < 0) {
      return { success: false, error: 'Bomb pot ante cannot be negative' };
    }
    if (finalSpecialRules.straddleMin > finalSpecialRules.straddleMax) {
      return { success: false, error: 'Straddle min cannot be greater than max' };
    }

    // Generate tags
    const tags = generateTags(finalSpecialRules, isPrivate, currency, voiceChatEnabled);

    // Call server API to create table
    console.log('🎮 [EnhancedTableService] Creating table via API...');
    
    const result = await apiCall<{ success: boolean; tableId: string }>('/createEnhancedTable', {
      name: tableName,
      currency,
      smallBlind,
      bigBlind,
      minBuyIn: calculatedMinBuyIn,
      maxBuyIn: calculatedMaxBuyIn,
      maxSeats,
      isPrivate,
      chipMode: request.chipMode,
      privateCode: finalPrivateCode,
      specialRules: finalSpecialRules,
      voiceChatEnabled,
      tags,
      customName: customName?.trim(),
      stateCode, //  NEW: Pass state code for geo check

      //  NEW: Rake waiver from agent bypass code

      rakeWaived,
      rakeWaiverCode,
      rakeWaiverAgentId,
      //  PREMIUM: Table theme settings

      tableTheme,
      dealerImageUrl: tableTheme === 'premium' ? dealerImageUrl : undefined,
    });

    console.log(`✅ [EnhancedTableService] Created table ${result.tableId}: ${tableName}`);
    if (isPrivate) {
      console.log(`🔒 Private code: ${finalPrivateCode}`);
    }
    if (voiceChatEnabled) {
      console.log(`🎙️ Voice chat enabled`);
    }
    if (rakeWaived) {
      console.log(`💰 Rake waived by agent code: ${rakeWaiverCode}`);
    }
    if (hasAnySpecialRules(finalSpecialRules)) {
      console.log(`🎮 Special rules enabled:`, getEnabledRuleNames(finalSpecialRules));
    }
    if (tableTheme === 'premium') {
      console.log(`🎰 Premium table theme with dealer: ${dealerImageUrl || 'default'}`);
    }

    return { success: true, tableId: result.tableId };

  } catch (error: any) {
    console.error('❌ [EnhancedTableService] Error creating table:', error);
    return { success: false, error: error.message || 'Failed to create table' };
  }
}

// ============================================================================
// PRIVATE TABLE OPERATIONS (Read-only, no API needed)
// ============================================================================

/**
 * Find a private table by its access code
 */
export async function findPrivateTableByCode(
  code: string
): Promise<{ success: boolean; tableId?: string; table?: ExtendedTableData; error?: string }> {
  try {
    const tablesRef = collection(db, 'tables');
    const q = query(
      tablesRef,
      where('isPrivate', '==', true),
      where('privateCode', '==', code.trim().toUpperCase())
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      // Try lowercase match too
      const qLower = query(
        tablesRef,
        where('isPrivate', '==', true),
        where('privateCode', '==', code.trim())
      );
      const snapshotLower = await getDocs(qLower);
      
      if (snapshotLower.empty) {
        return { success: false, error: 'No table found with this code' };
      }
      
      const docData = snapshotLower.docs[0];
      return {
        success: true,
        tableId: docData.id,
        table: { id: docData.id, ...docData.data() } as ExtendedTableData,
      };
    }

    const docData = snapshot.docs[0];
    return {
      success: true,
      tableId: docData.id,
      table: { id: docData.id, ...docData.data() } as ExtendedTableData,
    };

  } catch (error: any) {
    console.error('❌ [EnhancedTableService] Error finding private table:', error);
    return { success: false, error: error.message || 'Failed to find table' };
  }
}

/**
 * Get a table by ID with extended data
 */
export async function getEnhancedTable(
  tableId: string
): Promise<ExtendedTableData | null> {
  try {
    const tableRef = doc(db, 'tables', tableId);
    const tableDoc = await getDoc(tableRef);
    
    if (!tableDoc.exists()) return null;
    
    return { id: tableDoc.id, ...tableDoc.data() } as ExtendedTableData;
  } catch (error) {
    console.error('❌ [EnhancedTableService] Error getting table:', error);
    return null;
  }
}

/**
 * Search tables with filters (read-only)
 */
export async function searchTables(
  filters: TableSearchFilters
): Promise<ExtendedTableData[]> {
  try {
    let q = query(collection(db, 'tables'));
    
    // Apply filters (Firestore only supports one inequality per query)
    if (filters.currency) {
      q = query(q, where('currency', '==', filters.currency));
    }
    if (filters.isPrivate !== undefined) {
      q = query(q, where('isPrivate', '==', filters.isPrivate));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    const snapshot = await getDocs(q);
    let tables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ExtendedTableData));
    
    // Client-side filtering for complex conditions
    if (filters.minBlind !== undefined) {
      tables = tables.filter(t => t.bigBlind >= filters.minBlind!);
    }
    if (filters.maxBlind !== undefined) {
      tables = tables.filter(t => t.bigBlind <= filters.maxBlind!);
    }
    if (filters.hasSeatsAvailable) {
      tables = tables.filter(t => (t.players?.length || 0) < t.maxSeats);
    }
    if (filters.hasSpecialRules) {
      tables = tables.filter(t => t.specialRules && hasAnySpecialRules(t.specialRules));
    }
    
    return tables;
  } catch (error) {
    console.error('❌ [EnhancedTableService] Error searching tables:', error);
    return [];
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// ============================================================================
// COMPLETE EXPORT
// ============================================================================

export const enhancedTableService = {
  // Create
  createEnhancedTable,
  // Read
  findPrivateTableByCode,
  verifyPrivateCode,
  getEnhancedTable,
  getExtendedTableData,
  searchTables,
  // Helpers
  generateTableName,
  hasAnySpecialRules,
  getEnabledRuleNames,
  getSpecialRuleIcon,
  getSpecialRuleShortName,
  formatSpecialRulesForDisplay,
  // Validation
  isValidPrivateCode,
  isValidTableName,
  isValidBlinds,
  isValidBuyInRange,
};

export default enhancedTableService;

// ============================================================================
// PRIVATE TABLE VERIFICATION
// ============================================================================

/**
 * Verify a private table code
 */
export async function verifyPrivateCode(
  tableId: string,
  code: string
): Promise<boolean> {
  try {
    const tableRef = doc(db, 'tables', tableId);
    const tableSnap = await getDoc(tableRef);
    
    if (!tableSnap.exists()) {
      return false;
    }

    const data = tableSnap.data();
    if (!data.isPrivate) {
      return true; // Public table, no code needed
    }

    return data.privateCode?.toUpperCase() === code.trim().toUpperCase();

  } catch (error) {
    console.error('❌ [EnhancedTableService] Error verifying private code:', error);
    return false;
  }
}

/**
 * Get extended table data with all fields
 */
export async function getExtendedTableData(
  tableId: string
): Promise<ExtendedTableData | null> {
  try {
    const tableRef = doc(db, 'tables', tableId);
    const tableSnap = await getDoc(tableRef);
    
    if (!tableSnap.exists()) {
      return null;
    }

    const data = tableSnap.data();
    
    return {
      id: tableId,
      name: data.name || 'Unnamed Table',
      customName: data.customName,
      ownerId: data.ownerId,
      gameVariant: data.gameVariant || 'texas_holdem',
      bettingStructure: data.bettingStructure || 'no_limit',
      smallBlind: data.smallBlind,
      bigBlind: data.bigBlind,
      minBuyIn: data.minBuyIn,
      maxBuyIn: data.maxBuyIn,
      maxSeats: data.maxSeats || 8,
      currency: data.currency,
      isPrivate: data.isPrivate || false,
      privateCode: data.privateCode,
      voiceChatEnabled: data.voiceChatEnabled || false,
      voiceChatParticipants: data.voiceChatParticipants || 0,
      specialRules: data.specialRules || DEFAULT_SPECIAL_RULES,
      players: data.players || [],
      playerBuyIns: data.playerBuyIns || {},
      readyPlayers: data.readyPlayers || [],
      status: data.status || 'waiting',
      pot: data.pot || 0,
      gameState: data.gameState,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      tags: data.tags || [],
      //  PREMIUM: Table theme settings

      tableTheme: data.tableTheme || 'classic',
      dealerImageUrl: data.dealerImageUrl,
    } as ExtendedTableData;

  } catch (error) {
    console.error('❌ [EnhancedTableService] Error getting table data:', error);
    return null;
  }
}

// ============================================================================
// SPECIAL RULES DISPLAY HELPERS  
// ============================================================================

/**
 * Get icon for a special rule
 */
export function getSpecialRuleIcon(ruleId: string): string {
  switch (ruleId) {
    case 'sevenTwoGame': return '';

    case 'bombPotEnabled': return '';

    case 'straddleEnabled': return '⬆';

    case 'runItTwiceEnabled': return '';

    case 'allInOrFold': return '';

    case 'voiceChatEnabled': return '';

    default: return '';

  }
}

/**
 * Get short display name for a special rule
 */
export function getSpecialRuleShortName(ruleId: string): string {
  switch (ruleId) {
    case 'sevenTwoGame': return '7-2';
    case 'bombPotEnabled': return 'Bomb';
    case 'straddleEnabled': return 'Straddle';
    case 'runItTwiceEnabled': return 'Run 2x';
    case 'allInOrFold': return 'AoF';
    case 'voiceChatEnabled': return 'Voice';
    default: return ruleId;
  }
}

/**
 * Format special rules for display as badges
 */
export function formatSpecialRulesForDisplay(rules: SpecialRules): Array<{
  icon: string;
  name: string;
  id: string;
}> {
  const display: Array<{ icon: string; name: string; id: string }> = [];
  
  if (rules.sevenTwoGame) {
    display.push({ icon: '', name: '7-2', id: 'sevenTwoGame' });

  }
  if (rules.bombPotEnabled) {
    display.push({ icon: '', name: 'Bomb Pot', id: 'bombPotEnabled' });

  }
  if (rules.straddleEnabled) {
    display.push({ icon: '⬆', name: 'Straddle', id: 'straddleEnabled' });

  }
  if (rules.runItTwiceEnabled) {
    display.push({ icon: '', name: 'Run 2x', id: 'runItTwiceEnabled' });

  }
  if (rules.allInOrFold) {
    display.push({ icon: '', name: 'AoF', id: 'allInOrFold' });

  }
  
  return display;
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate private table code format
 */
export function isValidPrivateCode(code: string): boolean {
  if (!code || typeof code !== 'string') return false;
  const trimmed = code.trim();
  return trimmed.length >= 1 && trimmed.length <= 20;
}

/**
 * Validate table name
 */
export function isValidTableName(name: string): boolean {
  if (!name || typeof name !== 'string') return true;
  const trimmed = name.trim();
  return trimmed.length <= 50;
}

/**
 * Validate blinds
 */
export function isValidBlinds(smallBlind: number, bigBlind: number): {
  valid: boolean;
  error?: string;
} {
  if (smallBlind <= 0) {
    return { valid: false, error: 'Small blind must be greater than 0' };
  }
  if (bigBlind <= 0) {
    return { valid: false, error: 'Big blind must be greater than 0' };
  }
  if (bigBlind < smallBlind) {
    return { valid: false, error: 'Big blind must be at least equal to small blind' };
  }
  if (bigBlind > smallBlind * 4) {
    return { valid: false, error: 'Big blind should not exceed 4x small blind' };
  }
  return { valid: true };
}

/**
 * Validate buy-in range
 */
export function isValidBuyInRange(
  minBuyIn: number,
  maxBuyIn: number,
  bigBlind: number
): { valid: boolean; error?: string } {
  if (minBuyIn <= 0) {
    return { valid: false, error: 'Minimum buy-in must be greater than 0' };
  }
  if (maxBuyIn <= 0) {
    return { valid: false, error: 'Maximum buy-in must be greater than 0' };
  }
  if (minBuyIn > maxBuyIn) {
    return { valid: false, error: 'Minimum buy-in cannot exceed maximum buy-in' };
  }
  if (minBuyIn < bigBlind) {
    return { valid: false, error: 'Minimum buy-in should be at least 1 big blind' };
  }
  if (maxBuyIn > bigBlind * 500) {
    return { valid: false, error: 'Maximum buy-in should not exceed 500 big blinds' };
  }
  return { valid: true };
}